/**
 * Utility functions for PO Lines Tab
 */
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { PurchaseOrderLine } from './types';
import { mayReleaseHold, statusOf, viewerSideOf, type LineStatusFields } from '../../lineStatus';
import type { User } from '../../../../lib/api';

/**
 * True when the supplier has covered the remaining outstanding qty for
 * this line entirely from their own warehouse stock (delivered_warehouse_qty
 * >= outstanding_qty): everything not yet GRN'd is sitting in the supplier's
 * warehouse ready to dispatch. CR-7743: the warehouse share excludes what is
 * already shipped and awaiting receipt, so a line part on the road reads as
 * not yet fully in stock — as it did when the figure came from the ledger.
 *
 * Drives the supplier-only "In Stock" grey-out: lines in this state are
 * no longer actionable by the supplier (Accept / Propose / Hold suppressed)
 * but stay visible until the customer GRNs. Caller is responsible for the
 * isSupplier check — this predicate is portal-agnostic.
 */
export function isFullyInSupplierWarehouse(line: PurchaseOrderLine): boolean {
  const outstanding = parseFloat(line.outstanding_qty || '0') || 0;
  const inStock = parseFloat(line.delivered_warehouse_qty || '0') || 0;
  return outstanding > 0 && inStock >= outstanding;
}

/**
 * Get sort icon based on current sort state
 */
export function getSortIcon(
  field: string,
  sortField: string | null,
  sortDirection: 'asc' | 'desc'
) {
  if (sortField !== field) {
    return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
  }
  return sortDirection === 'asc' ? (
    <ArrowUp className="h-3 w-3 text-primary" />
  ) : (
    <ArrowDown className="h-3 w-3 text-primary" />
  );
}

/**
 * The line's status badge — ONE status, the hold included.
 *
 * EXPEDITING-PARITY PR 2 (Aaron, 17 Sep 2026): this took a status STRING, which
 * is what made a held line unrepresentable — the hold lives in other columns,
 * so the caller had to either ignore it or substitute it, and different callers
 * did different things. It takes the LINE now and asks `statusOf`, which is the
 * single owner of the answer.
 *
 * @param line - The PO line (or schedule row — same field names).
 * @returns The status badge element.
 */
export function getLineStatusBadge(line: LineStatusFields) {
  const { label, className, title } = statusOf(line);
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`} title={title}>
      {label}
    </span>
  );
}

/**
 * May this user take the line off hold?
 *
 * THE SIDE THE HOLD NAMES is the only side that releases it (G7, Aaron 17 Sep
 * 2026) — the placer by default, the other party when it was handed over. The
 * platform operator may always release; a row with no recorded placer stays
 * releasable so nothing can get stuck; no user at all defers to the server.
 *
 * The rule itself lives in `lineStatus.mayReleaseHold`, shared with both grids
 * and both modals and mirroring the server's `release_hold`. This JSDoc used to
 * say "customers and suppliers can release holds they placed", which is the rule
 * the body stopped implementing when G7 landed (comment lens, round 2).
 */
export interface HoldFields {
  on_hold_by_company_type?: string | null;
  /** G7: the side the placer nominated. Absent on holds placed before the ask. */
  hold_next_action_with?: string | null;
}

/**
 * A line nobody can act on any more: the goods are in, or the order is dead.
 *
 * CR-4697 ruled "no actions on closed (fully received) PO lines" and guarded
 * `received` in two predicates; CR-7418 (Aaron, 19 Sep) extends the same rule
 * to `cancelled` and to the HOLD control, which sat outside both guards and so
 * kept offering itself on lines nothing else would touch. One predicate, so the
 * three call sites cannot drift apart again.
 *
 * @param line - the PO line (or any row carrying `status`)
 * @returns true when no row action applies
 */
export function lineIsFinished(line: { status?: string | null }): boolean {
  return line.status === 'received' || line.status === 'cancelled';
}

export function canReleaseHold(user?: User | null, line?: HoldFields): boolean {
  // EXPEDITING-PARITY PR 2 (G7), Aaron 17 Sep 2026 — ONE rule after he cut the
  // hold dialog to two options: THE SIDE THE HOLD NAMES IS THE ONLY SIDE THAT
  // RELEASES IT. With no nomination (every hold placed before the question
  // existed) that side is the placer, which is exactly what those rows have
  // always done, so nothing needed a backfill.
  //
  // The rule itself now lives in lineStatus.mayReleaseHold, with the schedule
  // grain's two surfaces. This copy read `helix` as customer-side, which hid
  // Release from the operator on a supplier's hold and offered it to a customer
  // on the operator's (C1 and the blind pass's F20, gate round 1).
  if (!user) return true;
  return mayReleaseHold(viewerSideOf(user), line || {});
}
